<script setup lang="ts">
    import { computed, nextTick, watch } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import SelectInput from '@/js/components/SelectInput.vue';
    import { useQueryGetBackOfficeTenants } from '@/js/services/api/backOffice/backOfficeQueries';
    import { useBackOfficeStore } from '@/js/stores/back-office';
    import { getDefaultTenantSlug, setOverrideTenantSlug } from '@/js/services/http';
    import EchoButton from '@/js/components/EchoButton.vue';
    import IconButton from '@/js/components/IconButton.vue';
    import { DatabaseIcon, XIcon } from '@lucide/vue';

    const { data: tenantsData } = useQueryGetBackOfficeTenants();
    const backOfficeStore = useBackOfficeStore();

    const tenants = computed(() => tenantsData.value ?? []);
    const selectedTenantSlug = computed({
        get: () => backOfficeStore.selectedTenantSlug ?? getDefaultTenantSlug(),
        set: async (slug) => {
            backOfficeStore.selectedTenantSlug = slug;
            await nextTick();
            window.location.reload();
        },
    });

    // Apply the selected back-office tenant to all outgoing requests.
    watch(
        () => backOfficeStore.selectedTenantSlug,
        (slug) => {
            setOverrideTenantSlug(slug);
        },
        { immediate: true }
    );
</script>

<template>
    <div
        class="fixed top-0 left-0 sm:left-16 right-0 z-50"
        :class="!backOfficeStore.toolbarVisible ? '-translate-y-full' : ''"
    >
        <div class="h-12.5 bg-gray-lightest px-4 py-2">
            <div class="flex items-center gap-3">
                <EchoButton route="/back-office" size="small" variant="secondary">
                    <template #icon-left><DatabaseIcon /></template>
                    Back office
                </EchoButton>

                <div class="flex items-center gap-3">
                    <div class="text-gray-dark">
                        <SelectInput
                            v-if="tenants.length > 0"
                            v-model="selectedTenantSlug"
                            aria-label="Manage tenant"
                            size="small"
                        >
                            <option v-for="tenant in tenants" :key="tenant.id" :value="tenant.name">
                                {{ tenant.name }}
                            </option>
                        </SelectInput>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex items-center justify-center absolute w-full">
            <div
                v-if="!backOfficeStore.toolbarVisible"
                class="bg-gray-dark text-white rounded-b-md"
            >
                <div>
                    <IconButton
                        area-size="w-12 h-6"
                        size="size-4"
                        @click="backOfficeStore.toolbarVisible = !backOfficeStore.toolbarVisible"
                    >
                        <DatabaseIcon />
                    </IconButton>
                </div>
            </div>
        </div>

        <div class="absolute top-0.5 right-0.5">
            <IconButton @click="backOfficeStore.toolbarVisible = !backOfficeStore.toolbarVisible">
                <XIcon class="size-full" />
            </IconButton>
        </div>
    </div>
</template>
